import React from 'react'
import { AlertCircle, CheckCircle, Info } from 'lucide-react'

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Field label
   */
  label?: string
  /**
   * Field description/helper text
   */
  description?: string
  /**
   * Error message
   */
  error?: string
  /**
   * Success message
   */
  success?: string
  /**
   * Warning message
   */
  warning?: string
  /**
   * Info message
   */
  info?: string
  /**
   * Required field indicator
   */
  required?: boolean
  /**
   * Field content
   */
  children: React.ReactNode
  /**
   * Label for screen readers
   */
  labelFor?: string
  /**
   * Field size
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Field layout
   */
  layout?: 'vertical' | 'horizontal'
}

const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      description,
      error,
      success,
      warning,
      info,
      required = false,
      children,
      labelFor,
      size = 'md',
      layout = 'vertical',
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'w-full'
    ]

    const sizeClasses = {
      sm: ['text-sm'],
      md: ['text-base'],
      lg: ['text-lg']
    }

    const layoutClasses = {
      vertical: ['space-y-2'],
      horizontal: ['flex', 'items-start', 'space-x-4', 'space-x-reverse']
    }

    const fieldClasses = [
      ...baseClasses,
      ...sizeClasses[size],
      ...layoutClasses[layout],
      className
    ].join(' ')

    const getMessageIcon = () => {
      if (error) return <AlertCircle className="h-4 w-4 text-red-500" />
      if (success) return <CheckCircle className="h-4 w-4 text-green-500" />
      if (warning) return <AlertCircle className="h-4 w-4 text-yellow-500" />
      if (info) return <Info className="h-4 w-4 text-blue-500" />
      return null
    }

    const getMessageColor = () => {
      if (error) return 'text-red-600'
      if (success) return 'text-green-600'
      if (warning) return 'text-yellow-600'
      if (info) return 'text-blue-600'
      return 'text-gray-500'
    }

    const getMessage = () => {
      return error || success || warning || info
    }

    const labelElement = label && (
      <label
        htmlFor={labelFor}
        className={`block font-medium text-gray-700 ${
          required ? 'after:content-["*"] after:text-red-500 after:mr-1' : ''
        }`}
      >
        {label}
      </label>
    )

    const descriptionElement = description && (
      <p className="text-sm text-gray-500">{description}</p>
    )

    const messageElement = getMessage() && (
      <div className={`flex items-center gap-2 text-sm ${getMessageColor()}`}>
        {getMessageIcon()}
        <span>{getMessage()}</span>
      </div>
    )

    if (layout === 'horizontal') {
      return (
        <div ref={ref} className={fieldClasses} {...props}>
          <div className="flex-shrink-0 w-1/3">
            {labelElement}
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
          <div className="flex-1">
            {children}
            {messageElement}
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={fieldClasses} {...props}>
        {labelElement}
        {descriptionElement}
        {children}
        {messageElement}
      </div>
    )
  }
)

FormField.displayName = 'FormField'

export { FormField }
